import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';

import history from 'helpers/history';
import {
  buildWhatsAppOrderMessage,
  isValidWhatsAppNumber,
  normalizeWhatsappNumber,
  openWhatsAppChat,
} from 'helpers/whatsappOrderMessage';

import LogoContainer from 'containers/LogoContainer';
import Button from 'components/commons/Button';

import successImage from './assets/success.png';

import './SuccessfulPurchase.scss';

const WHATSAPP_REDIRECT_SECONDS = 5;

const SuccessfulPurchase = ({
  guideNumber,
  orderId,
  whatsappNumber,
  whatsappMessage,
}) => {
  const [countdown, setCountdown] = useState(WHATSAPP_REDIRECT_SECONDS);
  const normalizedWhatsappNumber = normalizeWhatsappNumber(whatsappNumber);
  const canRedirectToWhatsapp = isValidWhatsAppNumber(normalizedWhatsappNumber);
  const [showCountdown, setShowCountdown] = useState(canRedirectToWhatsapp);

  useEffect(() => {
    setShowCountdown(canRedirectToWhatsapp);
    setCountdown(WHATSAPP_REDIRECT_SECONDS);
  }, [canRedirectToWhatsapp, whatsappNumber]);

  useEffect(() => {
    if (!canRedirectToWhatsapp || !showCountdown) return undefined;

    if (countdown <= 0) {
      setShowCountdown(false);
      redirectToWhatsApp();
      return undefined;
    }

    const timer = setTimeout(() => setCountdown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown, canRedirectToWhatsapp, showCountdown]);

  const onShippingLinkClick = () => {
    history.push(`/seguimiento-de-envio/${guideNumber}`);
  };

  const redirectToWhatsApp = () => {
    setShowCountdown(false);

    if (!isValidWhatsAppNumber(normalizedWhatsappNumber)) {
      Swal.fire({
        icon: 'error',
        title: 'Número de WhatsApp no disponible',
        text: 'Por favor, contactá al soporte.',
      });
      return;
    }

    const message = whatsappMessage || buildWhatsAppOrderMessage(null, orderId);
    openWhatsAppChat(normalizedWhatsappNumber, message);
  };

  return (
    <div className="container" data-testid="successful-purchase-page">
      <div className="row successful-purchase flex-column justify-content-center align-items-center text-center">
        <img src={successImage} alt="successful-purchase" className="successful-purchase-check" />
        <p className="successful-purchase-title">¡MUCHAS GRACIAS!</p>
        <p className="successful-purchase-title">¡Tu pedido fue realizado con éxito!</p>

        {showCountdown && countdown > 0 && canRedirectToWhatsapp && (
          <p className="redirect-main-message">
            Redirigiendo a WhatsApp en <strong>{countdown}</strong> segundos.
          </p>
        )}

        <p>
          <span>
            En pocos minutos se le estará enviando un mail a tu casilla de correo electrónico
            {' '}
            con el detalle de tu pedido. Recordá verificar la carpeta de “SPAM” o
            “correo no deseado”.
          </span>
          <br />
          <span>
            En breve nos estaremos comunicando con vos para coordinar forma de pago
            {guideNumber ? '.' : ' y envío.'}
          </span>
          {guideNumber && (
            <>
              <br />
              <span>
                Su numero de envío es: <strong>{guideNumber}</strong>.
              </span>
              {' '}
              <span className="successful-purchase-shipping-link" onClick={onShippingLinkClick} role="presentation">
                Puede consultar el estado de su envío haciendo click acá.
              </span>
            </>
          )}
        </p>
        <p className="successful-purchase-title">Gracias por confiar en nosotros.</p>
        <div className="successful-purchase-logo">
          <LogoContainer />
        </div>
        {canRedirectToWhatsapp && (
          <div className="successful-purchase-whatsapp-btn">
            <Button text="Ir a WhatsApp" onClick={redirectToWhatsApp} />
          </div>
        )}
        <Button text="Volver al inicio" href="/home" />
      </div>
    </div>
  );
};

SuccessfulPurchase.propTypes = {
  guideNumber: PropTypes.string,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  whatsappNumber: PropTypes.string,
  whatsappMessage: PropTypes.string,
};

SuccessfulPurchase.defaultProps = {
  guideNumber: null,
  orderId: null,
  whatsappNumber: null,
  whatsappMessage: null,
};

export default SuccessfulPurchase;
